// api/suspensionApi.js
// Plain (unencrypted) JSON convention, same as deleteUserById in
// includes/auth/index.js -- the suspension endpoints don't need the
// encrypt/decrypt round-trip used by Animationsettingsapi.js.
import { elakay_API } from "../../config";

let __elakayInfoSource;
export const __setSuspensionInfoSource = (infoSource) => { if (infoSource) __elakayInfoSource = infoSource; };
const __elakayText = (key, fallback) =>
  __elakayInfoSource?.laguangeElakay?.("frontElakayHardcodedText", __elakayInfoSource.languageAPI)?.["suspensionapi"]?.[key] || fallback;

const headers = (token) => ({
  "Content-Type": "application/json",
  ...(token && { Authorization: `Bearer ${token}` }),
});

const handle = async (response) => {
  const data = await response.json();
  if (!response.ok) {
    const error = new Error(data.error || __elakayText("t001", "Network or server error."));
    error.status = response.status;
    error.errorCode = data.errorCode;
    error.suspension = data.suspension;
    throw error;
  }
  return data;
};

// POST /suspension/:userId -- create (and supersede any current one).
export const createSuspension = async (token, userId, payload) => {
  try {
    const response = await fetch(`${elakay_API}/suspension/${userId}`, {
      method: "POST",
      headers: headers(token),
      body: JSON.stringify(payload),
    });
    return await handle(response);
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// PATCH /suspension/manage/:id -- modify level/dates/reason/description.
export const updateSuspension = async (token, id, patch) => {
  try {
    const response = await fetch(`${elakay_API}/suspension/manage/${id}`, {
      method: "PATCH",
      headers: headers(token),
      body: JSON.stringify(patch),
    });
    return await handle(response);
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// POST /suspension/manage/:id/lift -- immediate reactivation.
export const liftSuspension = async (token, id, reason = "") => {
  try {
    const response = await fetch(`${elakay_API}/suspension/manage/${id}/lift`, {
      method: "POST",
      headers: headers(token),
      body: JSON.stringify({ reason }),
    });
    return await handle(response);
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// GET /suspension/user/:userId/history -- full audit history for the admin
// Suspension tab.
export const getSuspensionHistory = async (token, userId) => {
  try {
    const response = await fetch(`${elakay_API}/suspension/user/${userId}/history`, {
      method: "GET",
      headers: headers(token),
    });
    return await handle(response);
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// GET /suspension/me/active -- the logged-in user's own current suspension
// (or null).
export const getActiveSuspensionForMe = async (token) => {
  try {
    const response = await fetch(`${elakay_API}/suspension/me/active`, {
      method: "GET",
      headers: headers(token),
    });
    return await handle(response);
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// GET /suspension/:id -- single suspension detail.
export const getSuspensionById = async (token, id) => {
  try {
    const response = await fetch(`${elakay_API}/suspension/${id}`, {
      method: "GET",
      headers: headers(token),
    });
    return await handle(response);
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// POST /suspension/:id/respond -- the suspended user submits their
// explanation (Phase 3's response page will call this).
export const respondToSuspension = async (token, id, text) => {
  try {
    const response = await fetch(`${elakay_API}/suspension/${id}/respond`, {
      method: "POST",
      headers: headers(token),
      body: JSON.stringify({ text }),
    });
    return await handle(response);
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// GET /suspension/points/:userId -- "Ticket Suspension Score" for
// Community Management > View User.
export const getTicketSuspensionScore = async (token, userId) => {
  try {
    const response = await fetch(`${elakay_API}/suspension/points/${userId}`, {
      method: "GET",
      headers: headers(token),
    });
    return await handle(response);
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// GET /suspension/points/:userId/history -- the point-movement ledger.
export const getTicketPointHistory = async (token, userId) => {
  try {
    const response = await fetch(`${elakay_API}/suspension/points/${userId}/history`, {
      method: "GET",
      headers: headers(token),
    });
    return await handle(response);
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// PATCH /suspension/points/movement/:id/invalidate -- admin invalidates a
// bad/duplicate/fraudulent movement.
export const invalidateTicketPointMovement = async (token, movementId, reason = "") => {
  try {
    const response = await fetch(`${elakay_API}/suspension/points/movement/${movementId}/invalidate`, {
      method: "PATCH",
      headers: headers(token),
      body: JSON.stringify({ reason }),
    });
    return await handle(response);
  } catch (error) {
    return { success: false, error: error.message };
  }
};
